<script setup>
import { scheduledReportsApi } from '@/api/ScheduledReports/ScheduledReportsApi.js'
import { onMounted, ref } from 'vue'
import LoadingSpinner from '@/components/Spinners/LoadingSpinner.vue'
import { useAuthStore } from '@/stores/authStore.js'
import PortalDataTable from '@/components/DataTable/PortalDataTable.vue'
import EndpointTableCellMenu from '@/components/Tables/EndpointTableCellMenu.vue'
import { DataTableEndpointDefinition } from '@/models/datatable/DataTableEndpointDefinition.js'
import { Util } from '@/models/utils/Util.js'
import RunScheduledReportModal from '@/views/Admin/Reports/ScheduledReports/RunScheduledReportModal.vue'
import { Toaster } from '@/models/utils/Toaster.js'
import EndpointTableListBox from '@/components/Tables/EndpointTableListBox.vue'
import { Format } from '@/models/utils/FormatUtils.js'
import CopyTextModal from '@/components/Alerts/CopyTextModal.vue'
import EditScheduledReportModal from './EditScheduledReportModal.vue'
import { ButtonSize, ButtonWidth } from '@/components/Forms/Buttons/SubmitButtonConstants.js'
import TextEntryModal from '@/components/Alerts/TextEntryModal.vue'

const toaster = new Toaster();
const authStore = useAuthStore()
const tenantSettings = ref(null)
const table = ref(null)
const isLoading = ref(false)
const isSaving = ref(false);
const isDeleting = ref(false);
const editOpen = ref(false);
const runOnceOpen = ref(false);
const activeReport = ref(null);
const showCopyModal = ref(false)
const dialogTitle = ref('')
const plainDialogMessage = ref('')
const formattedDialogMessage = ref('')
const reportToDelete = ref(null)

const columns = [
	{ field: "enabled", title: "Status", sort: true, width: '5%' },
	{ field: "report_type", title: "Type", sort: true, search: false },
	{ field: "name", title: "Name", sort: true, search: true },
	{ field: "description", title: "Description", sort: false, search: true },
	{ field: "recipients", title: "Recipients", sort: false, search: true, width: '5%', minWidth: '400px' },
	{ field: "run_at", title: "Next Run", sort: true, width: '5%' },
	{ field: "ran_at", title: "Last Run", sort: true, width: '5%' },
	{ field: "actions", title: "Actions", sort: false, search: false },
	{ field: "frequency", sort: true, search: true, hide: true },
]

const endpoints = [
	new DataTableEndpointDefinition({
		endpointRoute: 'scheduled-reports/admin/reports',
		searchPlaceholder: 'Search',
		columns: columns,
		initialSortColumn: 'default',
	})
]

/**
 * Loads roles from the API.
 */
onMounted(() => {
	tenantSettings.value = authStore.getAuth.tenantSettings().scheduled_reports
})

/**
 * Gets the menu items for a scheduled report.
 * @returns {*[]}
 */
function getMenuItems(report)
{
	let items = [];
	Util.addActionMenuItem(items, 'edit', 'Edit')
	Util.addActionMenuItem(items, 'run','Run Once')
	Util.addActionMenuItem(items, 'duplicate','Duplicate')

	if (report.deletable) {
		Util.addActionMenuItem(items, 'delete','Delete')
	}
	return items;
}

/**
 * Handles menu item selection.
 * @param p_menu_item
 * @param p_record
 */
function onMenuItemSelected(p_menu_item, p_record)
{
	switch (p_menu_item.name)
	{
		case "edit":
		case "run":
			activeReport.value = p_record
			break
		case "duplicate": {
			const duplicateRecord = Util.deepCopy(p_record)
			duplicateRecord.source_report_id = p_record.id
			duplicateRecord.id = null
			duplicateRecord.ran_at = null
			activeReport.value = duplicateRecord
			break
		}
		case "delete":
			reportToDelete.value = p_record
			break
	}

	toggleModal(p_menu_item.name, true)
}

/**
 * Deletes a scheduled report.
 * @param reasonForDeletion
 */
function deleteReport(reasonForDeletion)
{
	scheduledReportsApi.deleteReport({
		'id': reportToDelete.value.id,
		'reason': reasonForDeletion
	}, onStatusUpdated)
	reportToDelete.value = null
}

/**
 * Toast to show if a report has been edited or run.
 * @param p_type
 * @param p_reload_eh
 */
function onCloseModal(p_type, p_reload_eh)
{
	toggleModal(p_type, false)

	if (p_reload_eh)
	{
		switch(p_type)
		{
			case "run":
				toaster.success("Your report has been successfully queued.")
				break

			case "edit":
				toaster.success("Your report has been successfully updated.")
				break

			case "duplicate":
				toaster.success("Your report has been successfully created.")
				break
		}
		reloadTable()
	}
}

/**
 * Show/hide the modal based on the type.
 * @param p_type Type that defines which modal is to be shown/opened
 * @param p_show_eh True to show, false to hide
 */
function toggleModal(p_type, p_show_eh)
{
	switch(p_type)
	{
		case "edit":
		case "duplicate":
			editOpen.value = p_show_eh
			break
		case "run":
			runOnceOpen.value = p_show_eh
			break
	}
}

/**
 * Reloads the table to get the latest data.
 */
function reloadTable()
{
	//this will break of isLoading is true
	if (table.value)
	{
		table.value.loadData()
	}
	else
	{
		location.reload()
	}
}

/**
 * Options available for scheduled report status.
 * @returns {*[]}
 */
function statusColumnData()
{
	let items = [];

	items.push({ id: true, text:'Active' })
	items.push({ id: false, text:'Disabled' })

	return items;
}

/**
 * Handle status item selection.
 * @param p_item Selected item
 */
function toggleReportEnabled(p_item)
{
	const enabled = p_item.text === "Active"

	if (enabled) {
		scheduledReportsApi.enableReport(p_item.record_id, onStatusUpdated)
	}
	else {
		scheduledReportsApi.disableReport(p_item.record_id, onStatusUpdated)
	}
}

/**
 * Handle response from a scheduled report status update.
 * @param p_response Response from server
 * @param p_type Type of update
 * @param p_error_eh Indicates an error occurred while updating scheduled report status.
 */
function onStatusUpdated(p_response, p_type, p_error_eh)
{
	let success_message = "";
	let failed_message = "";
	try
	{
		switch (p_type)
		{
			case "edit":
				success_message = "Report status updated successfully.";
				failed_message = "Failed to update report status. Please try again.";
				break;
			case "delete":
				success_message = "Report deleted successfully.";
				failed_message = "Failed to delete report. Please try again.";
				break;
			case "duplicate":
				success_message = "Report created successfully.";
				failed_message = "Failed to create report. Please try again.";
				break;
		}

		if (p_error_eh) //Uncaught error
		{
			p_response.showErrorBanner()
		}
		else
		{
			const data = p_response.getData();
			if (data && data.success)
			{
				toaster.success(success_message)
				reloadTable()
			}
			else if (data && !data.success) //Error
			{
				toaster.error(data.error_message)
			}
			else
			{
				toaster.error(failed_message)
			}
		}
	}
	catch (e)
	{
		toaster.error(failed_message)
		if (authStore.isDeveloper) console.error(e)
	}
}

function showTextDialog(title, message) {
	dialogTitle.value = title
	plainDialogMessage.value = message
	formattedDialogMessage.value = message
	showCopyModal.value = true
}

/**
 *
 * @param {Array<{ full_name: string, email: string}>} p_recipients
 */
function showRecipients(p_recipients)
{
	let recipients = '';

	for (let i = 0; i < p_recipients.length; i++)
	{
		if (p_recipients[i].full_name)
		{
			recipients += p_recipients[i].full_name + ' (' +	p_recipients[i].email + ")\r\n";
		}
		else
		{
			recipients += p_recipients[i].email + "\r\n";
		}
	}

	showTextDialog('Recipients', recipients)
}

</script>

<template>
	<LoadingSpinner v-if="isLoading" />
	<div v-if="!isLoading">
		<PortalDataTable
			ref="table"
			:endpoints="endpoints"
		>
			<template #enabled="data">
				<div v-if="data.value.error_message !== null">
					<div v-if="!authStore.isDeveloper" class="line-clamp-4 !text-error clickable-text overflow-ellipsis flex justify-center items-center min-w-22 py-1 px-3 rounded-md whitespace-nowrap bg-error-light text-error-on-color" @click="showTextDialog('Error', data.value.error_message)">
						Error
					</div>
					<div v-else class="flex justify-center items-center min-w-22 py-1 px-3 rounded-md whitespace-nowrap bg-error-light text-error-on-color">
						Error
					</div>
				</div>
				<div v-else class="flex w-full justify-center whitespace-nowrap">
					<EndpointTableListBox
						:record-id="data.value.id"
						:items="statusColumnData()"
						v-model="data.value.active_status"
						:showSelectedCheckmark="false"
						width="w-26"
						@onItemSelected="toggleReportEnabled"
						:displayed-item-c-s-s="{ 'Disabled': '!bg-disabled-subtle !text-label', 'Active': '!bg-primary-lighter !text-label'}"
					/>
				</div>
			</template>

			<template #name="data">
				{{ data.value.name}}
			</template>

			<template #description="data">
				<div class="whitespace-pre-wrap">
					{{ data.value.description}}
				</div>
			</template>

			<template #recipients="data">
				<div v-if="data.value.recipients && data.value.recipients.length > 0" class="clickable-text overflow-ellipsis whitespace-nowrap" @click="showRecipients(data.value.recipients)">
					<div v-for="(recipient, index) in data.value.recipients" :key="index">
						<span :class="[{'line-through': !recipient.is_active}]"><b>{{ recipient.full_name }}</b> ({{ recipient.email }})</span><span v-if="!recipient.is_active"> (Inactive)</span>
					</div>
				</div>
				<div v-else>
					There are no active recipients for this report.
				</div>
			</template>

			<template #run_at="data">
				<div class="flex w-full justify-center whitespace-nowrap">
					<div v-if="data.value.active_status.active && data.value.run_at">
						{{ Format.date(data.value.run_at) }}
					</div>
					<div v-else>
						-
					</div>
				</div>
			</template>

			<template #ran_at="data">
				<div class="flex w-full justify-center whitespace-nowrap">
					<div v-if="data.value.ran_at">
						{{ Format.date(data.value.ran_at) }}
					</div>
					<div v-else>
						-
					</div>
				</div>
			</template>

			<template #actions="data">
				<div class="flex w-full justify-center">
					<EndpointTableCellMenu
						:items="getMenuItems(data.value)"
						:record="data.value"
						@onMenuItemSelected="onMenuItemSelected"
					/>
				</div>
			</template>
		</PortalDataTable>
	</div>

	<EditScheduledReportModal
		v-if="editOpen"
		:open="true"
		:report="activeReport"
		:saving="isSaving"
		@evCloseModal="onCloseModal"
	/>

	<RunScheduledReportModal
		v-if="runOnceOpen"
		:open="true"
		:report="activeReport"
		:saving="isSaving"
		@evCloseModal="onCloseModal"
	/>

	<CopyTextModal
		v-if="showCopyModal"
		:show="true"
		:title="dialogTitle"
		:message="plainDialogMessage"
		:formattedMessage="formattedDialogMessage"
		@close="showCopyModal=false"
	/>

	<Teleport to="html">
		<TextEntryModal
			v-if="reportToDelete != null"
			:show="true"
			title="Delete Report"
			:twoButtonModal="true"
			:button-size="ButtonSize.Small"
			:button-width="ButtonWidth.Full"
			button-text="Delete"
			:loading="isDeleting"
			@onButtonClicked="deleteReport"
			@dismiss="reportToDelete = null"
			:max-length="250"
			message="Please enter the reason for deletion."
			sub-input-message="Warning: Deleting this report is permanent and cannot be undone!"
			:allow-empty="false"
		/>
	</Teleport>
</template>

<style scoped>

</style>